import useAxios from "@hooks/use-axios";
import { confirmAlert, toastify } from "@helpers/toast";
import { BRAND_ENDPOINTS } from "@constants/endpoints";
import { SIGN_COPY } from "../misc/constants";

const payloadOf = (res) => {
    const body = res?.data ?? res;
    return body?.data ?? body;
};

/**
 * The brand's signature on its publishers' contracts — one at a time, after
 * reviewing it, or every `awaiting_brand` contract at once. Signing one is
 * synchronous; Sign all only queues the work, and the list polls until it has
 * caught up. `onDone` is what makes the list re-read.
 *
 * Neither endpoint takes a body: the server stamps the signature itself.
 * @param brandId
 * @param onDone
 */
const useBrandSign = (brandId, onDone) => {
    const { axiosInstance: signInstance, loading: signing } = useAxios({ base: "brand", showAlert: false });
    const { axiosInstance: signAllInstance, loading: signingAll } = useAxios({ base: "brand", showAlert: false });

    const fail = (error) => toastify({ title: error?.response?.data?.message || SIGN_COPY.failure, type: "error" });

    const signOne = async (contractId) => {
        if (!brandId || !contractId) return false;

        try {
            await signInstance.post(BRAND_ENDPOINTS.CONTRACT_SIGN(brandId, contractId));
            toastify({ title: SIGN_COPY.signed, type: "success" });
            return true;
        } catch (error) {
            fail(error);
            return false;
        } finally {
            // A 409 means it moved on elsewhere — re-reading is what brings
            // the row in line either way.
            onDone?.();
        }
    };

    const signAll = async () => {
        try {
            const data = payloadOf(await signAllInstance.post(BRAND_ENDPOINTS.CONTRACT_SIGN_ALL(brandId)));
            toastify({ title: SIGN_COPY.allResult(data || {}), type: data?.failed ? "warning" : "success" });
        } catch (error) {
            fail(error);
        } finally {
            onDone?.();
        }
    };

    const handleSignAll = (count) => confirmAlert({
        title            : SIGN_COPY.allConfirmTitle(count),
        text             : SIGN_COPY.allConfirmText,
        confirmButtonText: SIGN_COPY.allConfirmButton,
        preConfirm       : signAll
    });

    return { signing, signingAll, signOne, handleSignAll };
};

export default useBrandSign;
